import type { GridColDef } from '@mui/x-data-grid-pro'
import Button from 'ext-ui/components/Button/Button'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  text_colorTextPrimary,
} from 'ext-ui/style/colors'
import {
  font_family_weight_regular,
  font_size_md,
} from 'ext-ui/style/typography'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import DataGrid, {
  DataGridHeaderStyle,
  DataGridRowHeight,
} from 'src/components/DataGrid/DataGrid'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
// Shared with the stats roll-up and the recent-transactions panel so the tab has
// ONE navigation idiom and the modifier-click guard lives in one place.
import { isPlainLeftClick } from 'src/feature/CardOverviewStats/navigationClick'
import StatNavLink from 'src/feature/CardOverviewStats/StatNavLink'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import PluralTrns from 'src/localization/PluralTrns'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import type { BudgetVirtualCardCounts } from 'src/queries/statistics/budgetVirtualCardCounts.queries'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { InfiniteScrollProps } from 'src/types'

import * as style from './CardBudgetsTable.style'

/** `DataGrid` hard-codes `columnHeaderHeight={44}`. */
const HEADER_HEIGHT = 44
/**
 * `DataGrid` renders its grid at `height - 60`, an inset sized for the full-page
 * tables it was built for (toolbar + footer). The snapshot has neither — its
 * footer is hidden — so the inset is added back rather than left as dead space
 * under the last row.
 */
const DATA_GRID_HEIGHT_INSET = 60

/** The em dash a missing count or amount renders as, never a fabricated zero. */
const NO_VALUE = '—'

type CardBudgetsTableRow = {
  id: string
  budget: CreditCardListModel
  /** Active virtual cards on this budget; undefined until the counts land. */
  cardCount?: number
  /**
   * Whether the current user may open this budget's detail page.
   *
   * A budget can sit on this card while belonging to an org the user has not
   * joined (a pending invite, or they left an org that still has budgets here).
   * Legacy rendered an inline "Action Required" panel for that case; this drops
   * the panel but keeps the gate, so the row never navigates somewhere the
   * user cannot open.
   */
  canView: boolean
}

export type CardBudgetsTableProps = {
  className?: string
  /** The budgets to render, already filtered and ordered by the caller. */
  budgets: CreditCardListModel[]
  /**
   * Budget id → active virtual-card count. A budget missing from the map renders
   * an em dash; the column is never withheld and never guesses a zero.
   */
  cardCounts: BudgetVirtualCardCounts
  /** True while the first page of budgets is in flight. */
  isPending: boolean
  /** True when the budget list request failed. */
  isError?: boolean
  /**
   * The count shown opposite the heading ("2 budgets"). Omitted on the Overview
   * snapshot, which carries a "view all" link in that slot instead.
   */
  totalCount?: number
  /**
   * Destination for the heading's "view all" link, built by the mounting page —
   * features do not know URLs. Omitted when there is no Budgets tab to link to,
   * which leaves the heading intact and drops only the link.
   */
  viewAllHref?: string
  /** SPA navigation, supplied by the page. See `StatNavLink`. */
  onNavigate?: (href: string) => void
  /**
   * Builds a row's destination. Supplied by the page — features do not know
   * URLs — so the budget-name cell can be a real `<a href>` rather than a
   * click handler.
   *
   * That is what gives rows ⌘-click / middle-click / "open in new tab",
   * right-click → copy link, a hover status-bar preview, and a place in a
   * screen reader's link list. `onSelectBudget` still handles the plain click
   * anywhere else in the row.
   */
  budgetHref: (budgetId: string) => string
  /** Selecting a row. The page decides what that means (navigate to the budget). */
  onSelectBudget: (budgetId: string) => void
  /**
   * Starting the create-budget flow. Omitted where that flow is unavailable —
   * a card that cannot issue budgets, or a user who must set up an org first —
   * which drops the CTA rather than offering a dead end. The page owns that
   * decision because the answer is account-level, not table-level.
   */
  onCreateBudget?: () => void
  /**
   * Paging state for the full Budgets tab. Omitted on the Overview snapshot,
   * which renders a fixed three rows and never pages.
   */
  infiniteScrollProps?: InfiniteScrollProps<Record<string, never>>
  /**
   * Explicit grid height for the full Budgets tab, which fills the viewport.
   * Omitted on the snapshot, which sizes itself to its rows.
   */
  height?: number
}

/**
 * The redesigned budget table for the card-detail page (EX-40737).
 *
 * ONE component for both places the design shows it — the Overview tab's
 * three-row snapshot and the Budgets tab's full list — because they are the same
 * table under two headings. The differences are all props: the snapshot passes
 * `viewAllHref` and sizes itself to its rows, the full list passes `totalCount`
 * and `infiniteScrollProps` and takes an explicit height.
 *
 * Columns, in design order: Budget (card-type glyph + name) · Type · Cards ·
 * Owner · Spent / limit. That last pair replaces legacy's separate Exposure and
 * Remaining columns, which reported the same money in a shape the design drops.
 *
 * Routing-free by the `src/feature/**` boundary: the page supplies the "view
 * all" destination and the row-selection handler.
 */
export default function CardBudgetsTable(
  props: CardBudgetsTableProps,
): ReactElement {
  const {
    className,
    budgets,
    cardCounts,
    isPending,
    isError = false,
    totalCount,
    viewAllHref,
    onNavigate,
    budgetHref,
    onSelectBudget,
    onCreateBudget,
    infiniteScrollProps,
    height,
  } = props

  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const users = useSelector(selectUserListModelsFromState)
  const currentUser = useCurrentUser()

  const rows = useMemo(
    (): CardBudgetsTableRow[] =>
      budgets.map((budget) => ({
        id: budget.id,
        budget,
        cardCount: cardCounts[budget.id],
        // The same expression all three legacy budget tables compute
        // (BudgetsTableLarge, BudgetsTableSmall, BudgetDataGrid).
        canView:
          budget.organizationId === currentUser?.organizationId ||
          budget.ownerId === currentUser?.id,
      })),
    [budgets, cardCounts, currentUser?.organizationId, currentUser?.id],
  )

  /**
   * "$1,100 / $20,000" — whole dollars, matching the design. A budget with no
   * issuing limit reads "Unlimited" on the right of the slash rather than an
   * invented ceiling; that is the same word legacy's Remaining column used for
   * the same state.
   */
  const spentOverLimit = useCallback(
    (budget: CreditCardListModel): string => {
      const spent = currencyFormatter({
        amount: budget.totalActiveSpent,
        currency: budget.currency,
        maximumFractionDigits: 0,
      })
      const limit =
        budget.issuingLimit !== undefined
          ? currencyFormatter({
              amount: budget.issuingLimit,
              currency: budget.currency,
              maximumFractionDigits: 0,
            })
          : translate('Unlimited')
      return `${spent} / ${limit}`
    },
    [currencyFormatter, translate],
  )

  /**
   * Supplies the header element instead of letting MUI render its own — see
   * `style.columnHeader` for why restyling MUI's is a cascade race. `headerName`
   * stays set alongside it, because that is what MUI reads for the column's
   * accessible name.
   */
  const renderHeader = useCallback(
    (label: string, align?: 'right') => (): ReactElement => (
      <span
        css={align === 'right' ? style.columnHeaderRight : style.columnHeader}
      >
        {label}
      </span>
    ),
    [],
  )

  const columns = useMemo(
    (): Array<GridColDef<CardBudgetsTableRow>> => [
      {
        field: 'budgetName',
        headerName: translate('Budget'),
        renderHeader: renderHeader(translate('Budget')),
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (params): ReactElement => {
          /*
            Decorative glyph: it encodes recurring-vs-standard and
            active-vs-inactive, both of which the Type column and the row's own
            state already say in words. Alt text would double-announce.
          */
          const content = (
            <>
              <img
                css={style.nameGlyph}
                src={params.row.budget.cardTypeImageSrc}
                alt=""
              />
              <span css={style.nameText}>
                {params.row.budget.truncatedDisplayName}
              </span>
            </>
          )

          // No anchor for a budget the user cannot open — a link that
          // dead-ends is worse than plain text.
          if (!params.row.canView) {
            return <span css={style.nameCellStatic}>{content}</span>
          }

          return (
            <a
              css={style.nameLink}
              href={budgetHref(params.row.budget.id)}
              onClick={(event): void => {
                // Modified clicks and non-primary buttons fall through to the
                // browser, so open-in-new-tab still works. Shared with
                // StatNavLink so the two cannot drift.
                if (!isPlainLeftClick(event)) return
                event.preventDefault()
                onSelectBudget(params.row.budget.id)
              }}
            >
              {content}
            </a>
          )
        },
      },
      {
        field: 'type',
        headerName: translate('Type'),
        renderHeader: renderHeader(translate('Type')),
        minWidth: 116,
        flex: 0.4,
        sortable: false,
        // `recurs` is the same field `cardTypeImageSrc` picks its glyph from, so
        // the word and the icon cannot disagree.
        //
        // `valueGetter` stays alongside `renderCell`: it is what MUI reads for
        // the cell's title tooltip and its accessible value.
        valueGetter: (_value, row): string =>
          row.budget.recurs ? translate('Recurring') : translate('Standard'),
        renderCell: (params): ReactElement => (
          <span css={style.textCell}>
            {params.row.budget.recurs
              ? translate('Recurring')
              : translate('Standard')}
          </span>
        ),
      },
      {
        field: 'cards',
        headerName: translate('Cards'),
        renderHeader: renderHeader(translate('Cards')),
        minWidth: 80,
        flex: 0.25,
        sortable: false,
        // Em dash, not 0: the counts arrive in their own request, and a budget
        // whose count has not landed (or whose request failed) has an unknown
        // number of cards, not none.
        valueGetter: (_value, row): string =>
          row.cardCount === undefined ? NO_VALUE : String(row.cardCount),
        renderCell: (params): ReactElement => (
          <span css={style.textCell}>
            {params.row.cardCount === undefined
              ? NO_VALUE
              : String(params.row.cardCount)}
          </span>
        ),
      },
      {
        field: 'owner',
        headerName: translate('Owner'),
        renderHeader: renderHeader(translate('Owner')),
        minWidth: 180,
        flex: 0.7,
        sortable: false,
        renderCell: (params): ReactElement | null => {
          const owner = users[params.row.budget.ownerId]
          return owner !== undefined ? <UserAvatarAndName user={owner} /> : null
        },
      },
      {
        field: 'spentLimit',
        headerName: translate('Spent / limit'),
        renderHeader: renderHeader(translate('Spent / limit'), 'right'),
        minWidth: 150,
        flex: 0.6,
        sortable: false,
        // Terminal column, right-aligned per the design. `align`/`headerAlign`
        // rather than CSS so MUI keeps the cell and its header in step.
        align: 'right',
        headerAlign: 'right',
        renderCell: (params): ReactElement => (
          <span css={style.amountCell}>
            {spentOverLimit(params.row.budget)}
          </span>
        ),
      },
    ],
    [
      translate,
      users,
      spentOverLimit,
      renderHeader,
      budgetHref,
      onSelectBudget,
    ],
  )

  /**
   * The full Budgets tab, as opposed to the Overview snapshot. Paging is the
   * honest discriminator: only the full list supplies it, and it is exactly the
   * property that decides whether the grid must virtualize and size itself
   * against a scrollbar.
   */
  const isPaged = infiniteScrollProps !== undefined

  const isEmpty = !isPending && !isError && rows.length === 0

  const body = (): ReactElement => {
    if (isPending) {
      return (
        <p css={style.statusBody} role="status">
          {translate('Loading budgets')}
        </p>
      )
    }

    if (isError) {
      return (
        <p css={style.statusBody} role="alert">
          {translate("We couldn't load budgets. Try again later.")}
        </p>
      )
    }

    if (isEmpty) {
      return (
        <p css={style.statusBody}>
          {translate('No budgets on this card yet.')}
        </p>
      )
    }

    return (
      <DataGrid<Record<string, never>, CardBudgetsTableRow>
        css={style.grid}
        rows={rows}
        columns={columns}
        filtersBody={{}}
        rowHeight={DataGridRowHeight.Default}
        // The design's header is a plain kicker row over a hairline, not the
        // shared grid's filled band.
        headerStyle={DataGridHeaderStyle.NoBackground}
        height={
          height ??
          HEADER_HEIGHT +
            rows.length * DataGridRowHeight.Default +
            DATA_GRID_HEIGHT_INSET
        }
        infiniteScrollProps={infiniteScrollProps}
        // Fixed column set, and all of it has to be said explicitly —
        // DataGridPro ships drag-reorder and resize on by default.
        // `disableColumnMenu` subsumes hiding and is the only one that removes
        // the ⋮ entirely, which matters because every column here is
        // `sortable: false` and a sort-only menu would open empty.
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        pinFirstColumn={false}
        checkboxSelection={false}
        // No pagination and no selection, so MUI's default footer contributes a
        // row total and ~52px of chrome that says nothing the heading doesn't.
        hideFooter
        // Only for the fixed-height snapshot, whose handful of rows never need
        // windowing and where virtualization is what makes the scroller's
        // measured height wobble as it settles — which is what makes page
        // scroll catch on the table.
        //
        // The full Budgets tab keeps it ON. That list pages without bound, and
        // an unvirtualized grid both renders every loaded row and mismeasures
        // its own width once a vertical scrollbar appears — which is what pushed
        // the terminal column under the scrollbar gutter.
        disableVirtualization={!isPaged}
        // ⚠️ `DataGrid` merges consumer `sx` with a SHALLOW spread, so any key
        // here REPLACES its rule rather than extending it. `.MuiDataGrid-cell`
        // is the one key below that the shared grid also sets, so its three
        // declarations are restated verbatim — dropping them collapses every
        // cell's flex centring and re-adds padding the row heights assume.
        //
        // These live in `sx` rather than the `css` prop because MUI injects its
        // own rules through emotion on the grid root; a styled-components
        // descendant selector is a specificity race against them, and `sx` is
        // the channel that reliably wins.
        sx={{
          // No resizing and no reordering here, so the grab handles the shared
          // grid keeps at `opacity: 1` have nothing to grab — they only read as
          // column dividers, which the design does not have.
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          // The shared grid insets headers by 16px while leaving cells at 0, so
          // every header sat a notch right of the column it labels. The design
          // aligns them, and the cells are the ones already flush with the
          // panel's content edge.
          '& .MuiDataGrid-columnHeader': {
            padding: 0,
            // ↓ restated from the shared grid — see the shallow-merge note.
            // The background matters: on the paged list the header is sticky
            // over scrolling rows, and a transparent one lets them show through.
            backgroundColor: base_colorRaisedSurface,
            borderLeftStyle: 'unset',
          },
          // One hairline under the whole header row; the shared grid marks the
          // band with a background fill instead, which NoBackground removes.
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: `1px solid ${base_colorContentBorder}`,
          },
          // The scrollbar gutter's filler cell carries a left border, which
          // reads as a stray rule floating past the last column.
          '& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-filler': {
            borderLeft: 'none',
          },
          // The snapshot is sized to exactly fit its rows, so it is never meant
          // to scroll — but the header's hairline and sub-pixel row heights
          // leave a pixel or two of scrollable range, which paints a scrollbar
          // and makes the scroller swallow wheel events the page should get.
          // The same fix, for the same reason, as the recent-transactions panel.
          //
          // The PAGED list must keep scrolling, so it inherits the shared rule
          // untouched.
          ...(isPaged
            ? {}
            : {
                '& .MuiDataGrid-virtualScroller': {
                  // ↓ restated from the shared grid — see the shallow-merge
                  // note. Losing `overscrollBehaviorX` in particular would let
                  // a horizontal trackpad swipe trigger browser BACK.
                  willChange: 'transform',
                  transform: 'translateZ(0)',
                  contain: 'layout paint',
                  backfaceVisibility: 'hidden',
                  overscrollBehaviorX: 'none',
                  // The shared grid `contain`s vertical overscroll so a tall,
                  // genuinely scrollable table does not scroll the page out
                  // from under it. This one is never scrollable, so that only
                  // gets in the way.
                  overscrollBehaviorY: 'auto',
                  overflowY: 'hidden',
                },
              }),
          '& .MuiDataGrid-cell': {
            // ↓ restated from the shared grid — see the shallow-merge note.
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            // Body cells sit at 14px regular; the default inherits a larger,
            // heavier face from the page.
            fontSize: font_size_md,
            fontFamily: font_family_weight_regular,
            color: text_colorTextPrimary,
          },
        }}
        onRowClick={(params, event): void => {
          /*
            The same guard the name-cell anchor uses, and it has to be here too.
            A ⌘-click on that anchor deliberately does NOT preventDefault — it
            lets the browser open a new tab — but the event still bubbles to
            this handler. Checking only `defaultPrevented` would navigate the
            CURRENT tab as well, so the user gets both.

            It also covers the plain-click case: the anchor preventDefaults and
            handles it, so this sees `defaultPrevented` and stays out of the way.
          */
          if (!isPlainLeftClick(event)) return
          if (!params.row.canView) return
          onSelectBudget(params.row.budget.id)
        }}
        // Row navigation must not be mouse-only (src/CLAUDE.md WCAG AA floor).
        // MUI gives cells a roving tabindex, so Enter on the focused cell is the
        // keyboard equivalent of clicking its row.
        onCellKeyDown={(params, event): void => {
          if (event.key !== 'Enter' || event.defaultPrevented) return
          if (!params.row.canView) return
          event.defaultMuiPrevented = true
          onSelectBudget(params.row.budget.id)
        }}
      />
    )
  }

  return (
    <ContentCard
      className={className}
      css={style.panel}
      data-testid="cardBudgetsTable"
    >
      <div css={style.header}>
        <h2 css={style.title}>{translate('Budgets')}</h2>
        <div css={style.headerActions}>
          {viewAllHref !== undefined && (
            <StatNavLink href={viewAllHref} onNavigate={onNavigate}>
              {/* Arrow lives in the source string, matching the sibling
                  CardOverviewStats links ("View budgets →", "Adjust limit →"). */}
              {translate('View all budgets →')}
            </StatNavLink>
          )}
          {viewAllHref === undefined && totalCount !== undefined && (
            <span css={style.count}>
              {/* A card with exactly one budget is reachable here — the
                  Overview snapshot takes the viewAllHref branch, so this count
                  only ever renders on the Budgets tab. */}
              <PluralTrns
                pluralSource={'${count} budgets'}
                singleSource={'1 budget'}
                count={totalCount}
              />
            </span>
          )}
          {/*
            The design's New budget CTA. Rendered only when the page supplies a
            handler, which is how a card that cannot issue budgets — or a user
            whose create flow needs an org set up first — gets the heading
            without a button that would dead-end them.
          */}
          {onCreateBudget !== undefined && (
            <Button
              variant="secondary"
              onClick={onCreateBudget}
              data-testid="cardBudgetsTable-createBudget"
            >
              {translate('New budget')}
            </Button>
          )}
        </div>
      </div>
      {body()}
    </ContentCard>
  )
}
